import { CalendarDays, Check, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { FamilyCalendar, StandardHoliday } from '../lib/types';
import { Fold, Section } from '../ui/Collapsible';
import { useParent } from './ParentContext';
import { Link } from 'react-router';

const pretty = (ymd: string) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
const rangeLabel = (s: string, e: string) => (s === e ? pretty(s) : `${pretty(s)} – ${pretty(e)}`);

/** Fired after the school year or its breaks change. */
export const SCHOOL_CALENDAR_CHANGED = 'cg:school-calendar-changed';

/** Settings card: the family's school-year range and its no-school holidays & breaks (#105, #106). */
export function SchoolCalendar({ embedded = false }: { embedded?: boolean } = {}) {
  const { toast } = useParent();
  const [cal, setCal] = useState<FamilyCalendar | null>(null);

  const load = useCallback(() => {
    api.get<FamilyCalendar>('/api/school-calendar').then(setCal).catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [toast]);
  useEffect(load, [load]);
  // Closing out a year (#623) sets next year's dates.
  useEffect(() => {
    window.addEventListener(SCHOOL_CALENDAR_CHANGED, load);
    return () => window.removeEventListener(SCHOOL_CALENDAR_CHANGED, load);
  }, [load]);
  // After a change, tell the report card schedule on the same page: its dates skip breaks (#359, #365).
  const changed = () => {
    load();
    window.dispatchEvent(new Event(SCHOOL_CALENDAR_CHANGED));
  };

  if (!cal) return null;

  const parts = (
    <>
      <Fold id="school-calendar" title="School year" summary={cal.schoolYearStart ? `${short(cal.schoolYearStart)} – ${short(cal.schoolYearEnd)}` : 'Not set'}>
        <SchoolYear cal={cal} reload={changed} />
      </Fold>
      <StateComplianceMoved />
      <Fold id="holidays" title="Holidays & breaks" summary={breaksSummary(cal)}>
        <p className="muted small">No school for the whole family on these days, and reports count them as vacation. Applies alongside each child’s weekly schedule.</p>
        <Holidays cal={cal} reload={changed} />
      </Fold>
    </>
  );
  // In School Settings (#601) its parts are folds of their own; on its own it's one section.
  if (embedded) return parts;
  return (
    <Section id="school-calendar-section" title="School calendar" icon={<CalendarDays size={18} />}>
      {parts}
    </Section>
  );
}

function SchoolYear({ cal, reload }: { cal: FamilyCalendar; reload: () => void }) {
  const { toast } = useParent();
  const [start, setStart] = useState(cal.schoolYearStart);
  const [end, setEnd] = useState(cal.schoolYearEnd);
  const dirty = start !== cal.schoolYearStart || end !== cal.schoolYearEnd;

  async function save(clear = false) {
    try {
      await api.put('/api/school-calendar', clear ? { start: '', end: '' } : { start, end });
      toast({ text: clear ? 'School year cleared.' : 'School year saved.', tone: 'good' });
      reload();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  return (
    <div className="stack">
      <div className="field-row">
        <label>
          First day
          <input type="date" value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)} />
        </label>
        <label>
          Last day
          <input type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} />
        </label>
      </div>
      <div className="row-gap">
        <button className="btn primary" onClick={() => save(false)} disabled={!dirty || !start || !end}>
          Save
        </button>
        {(cal.schoolYearStart || cal.schoolYearEnd) && (
          <button className="btn small" onClick={() => save(true)}>
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Holidays & breaks (#631): the school year's US holidays as chips that go on or off the calendar with a tap, then the
 * family's own breaks, upcoming first. Anything that has already ended hides under "Show past".
 */
function Holidays({ cal, reload }: { cal: FamilyCalendar; reload: () => void }) {
  const { toast } = useParent();
  const [options, setOptions] = useState<StandardHoliday[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  // The holidays in the school year; fetched again when its dates change.
  const year = `${cal.schoolYearStart}|${cal.schoolYearEnd}`;
  useEffect(() => {
    api
      .get<{ holidays: StandardHoliday[] }>('/api/school-calendar/standard-holidays')
      .then((r) => setOptions(r.holidays))
      .catch(() => setOptions([]));
  }, [year]);

  const key = (h: { name: string; start: string; end: string }) => `${h.name}|${h.start}|${h.end}`;
  const onCalendar = new Map(cal.breaks.map((b) => [key(b), b]));
  const holidays = options ?? [];
  const holidayKeys = new Set(holidays.map(key));
  const today = new Date().toISOString().slice(0, 10);
  const own = cal.breaks.filter((b) => !holidayKeys.has(key(b))).sort((a, b) => a.start.localeCompare(b.start));
  const upcoming = own.filter((b) => b.end >= today);
  const past = own.filter((b) => b.end < today).reverse();
  const yearLabel = cal.schoolYearStart && cal.schoolYearEnd ? ` · ${cal.schoolYearStart.slice(0, 4)}–${cal.schoolYearEnd.slice(2, 4)}` : '';

  async function addHolidays(list: StandardHoliday[]) {
    if (list.length) await api.post('/api/school-calendar/standard-holidays', { holidays: list.map(({ name, start, end }) => ({ name, start, end })) });
  }
  async function removeHolidays(list: StandardHoliday[]) {
    for (const h of list) {
      const b = onCalendar.get(key(h));
      if (b) await api.del(`/api/school-calendar/breaks/${b.id}`);
    }
  }
  async function run(work: () => Promise<unknown>) {
    setBusy(true);
    try {
      await work();
      reload();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  const toggle = (h: StandardHoliday) => run(() => (onCalendar.has(key(h)) ? removeHolidays([h]) : addHolidays([h])));
  const anyOn = holidays.some((h) => onCalendar.has(key(h)));
  const allOn = holidays.every((h) => onCalendar.has(key(h)));

  const add = () =>
    run(async () => {
      await api.post('/api/school-calendar/breaks', { name: name.trim(), start, end: end || start });
      setName('');
      setStart('');
      setEnd('');
      setAdding(false);
    });

  const row = (b: FamilyCalendar['breaks'][number]) => (
    <div key={b.id} className="row-between list-row">
      <span className="grow">
        <strong>{b.name}</strong> <span className="muted small">· {rangeLabel(b.start, b.end)}</span>
      </span>
      <button className="btn small danger-text" onClick={() => run(() => api.del(`/api/school-calendar/breaks/${b.id}`))} disabled={busy} title="Remove" aria-label={`Remove ${b.name}`}>
        <Trash2 size={14} />
      </button>
    </div>
  );

  return (
    <div className="stack">
      <div className="stack tight">
        <div className="row-between wrap">
          <strong>US holidays{yearLabel}</strong>
          {holidays.length > 0 && (
            <span className="row-gap">
              <button className="btn small" onClick={() => run(() => addHolidays(holidays.filter((h) => !onCalendar.has(key(h)))))} disabled={busy || allOn}>
                All
              </button>
              <button className="btn small" onClick={() => run(() => removeHolidays(holidays))} disabled={busy || !anyOn}>
                None
              </button>
            </span>
          )}
        </div>
        {!options ? (
          <p className="muted small">Loading…</p>
        ) : holidays.length === 0 ? (
          <p className="muted small">Set your school year first to see the holidays in it.</p>
        ) : (
          <>
            <div className="holiday-chips">
              {holidays.map((h) => {
                const on = onCalendar.has(key(h));
                return (
                  <button key={key(h)} className={`holiday-chip${on ? ' on' : ''}`} aria-pressed={on} disabled={busy} onClick={() => toggle(h)}>
                    <span className="holiday-chip-name">
                      {on && <Check size={13} />}
                      {h.name}
                    </span>
                    <span className="muted small">{chipDate(h.start, h.end)}</span>
                  </button>
                );
              })}
            </div>
            <span className="muted small">Tap one to put it on the calendar or take it off. Saved right away.</span>
          </>
        )}
      </div>

      <div className="stack tight">
        <div className="row-between wrap">
          <strong>Your breaks</strong>
          <button className="btn small" onClick={() => setAdding((a) => !a)} aria-expanded={adding}>
            <Plus size={14} /> Add a break
          </button>
        </div>
        {adding && (
          <div className="stack new-form">
            <label>
              Name
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="e.g. Spring break" />
            </label>
            <div className="field-row">
              <label>
                From
                <input type="date" value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)} />
              </label>
              <label>
                To <span className="muted small">(same as From for one day)</span>
                <input type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} />
              </label>
            </div>
            <button className="btn primary" onClick={add} disabled={busy || !name.trim() || !start}>
              Add
            </button>
          </div>
        )}
        {upcoming.length ? <div className="break-list">{upcoming.map(row)}</div> : <p className="muted small">No upcoming breaks.</p>}
        {past.length > 0 && (
          <>
            <button className="link-btn small past-toggle" onClick={() => setShowPast((v) => !v)} aria-expanded={showPast}>
              {showPast ? 'Hide past' : `Show past (${past.length})`}
            </button>
            {showPast && <div className="break-list past">{past.map(row)}</div>}
          </>
        )}
      </div>
    </div>
  );
}

/** A holiday chip's date without the year: "Sep 7", "Nov 26 – 27". */
function chipDate(s: string, e: string): string {
  const md = (ymd: string) => new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return s === e ? md(s) : `${md(s)} – ${md(e)}`;
}

const short = (ymd: string) => new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
/** The fold's summary (#601): how many, and the next one coming up. */
function breaksSummary(cal: FamilyCalendar): string {
  if (!cal.breaks.length) return 'None yet';
  const today = new Date().toISOString().slice(0, 10);
  const next = cal.breaks.filter((b) => b.end >= today).sort((a, b) => a.start.localeCompare(b.start))[0];
  const count = cal.breaks.length === 1 ? '1 holiday or break' : `${cal.breaks.length} holidays and breaks`;
  return next ? `${count} · next: ${next.name}` : count;
}

/** State compliance moved to each child's page (#954): opting in, the state and the documents are there now. */
function StateComplianceMoved() {
  const { children } = useParent();
  const first = children.find((c) => c.stateCompliance?.available);
  if (!first) return null;
  return (
    <Fold id="state-requirements" title="Your state’s requirements" summary={first.stateCompliance?.on ? 'On' : 'Off'}>
      <p className="muted small">
        State compliance (your state’s requirement, filing deadlines and documents) is in its own section on each child’s page.{' '}
        <Link to={`/kids/${first.id}/compliance`}>Open State compliance</Link>
      </p>
    </Fold>
  );
}
